// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface PopulateAutoCompleteContentStyles {
    contentWrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);

    return {
        searchQuery: location.search,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const populateAutoCompleteContentStyles: PopulateAutoCompleteContentStyles = {};

const styles = populateAutoCompleteContentStyles;

const ProductListPopulateAutoCompleteContent: FC<Props> = ({
    searchQuery,
    signInPageUrl,
    history,
    getAutocompleteModel,
}) => {
    const parsedQuery = parseQueryString<{ query: string }>(searchQuery);
    const parsedSearchQuery = parsedQuery.query;

    const [content, setContent] = useState<AutocompleteItemModel[] | null>();

    const loadAutocomplete = (query: string) => {
        getAutocompleteModel({
            query,
            signInPageUrl,
            onSuccess: (result: AutocompleteModel) => {
                setContent(result?.content);
            },
        } as any);
    };

    useEffect(() => {
        loadAutocomplete(parsedSearchQuery);
    }, [parsedSearchQuery]);

    if (!content?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.contentWrapper}>
            <Typography {...styles.headerText} data-test-selector="populate-autocomplete-content-header">
                {translate("Content")}
            </Typography>
            {content?.map(content => (
                <Link
                    key={content.url}
                    onClick={() => history.push(content.url)}
                    data-test-selector="populate-autocomplete-content-link"
                    {...styles.link}
                >
                    {content.displayTitle}
                </Link>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductListPopulateAutoCompleteContent)),
    definition: {
        group: "Product List",
        displayName: "Populate AutoComplete Content",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
